/**
 * The envelope page header's lead (TMMAPP-0101; audit 06 § B1, v2 L3712–3725): the narrative per kind,
 * from the served `balanceMinor`, `targetMinor` and `periodFigures` (nothing is derived here beyond the
 * one subtraction v2 draws for a goal). TMMAPP-0151 §G: the share line gave way to a privacy chip,
 * itself removed by TMMAPP-0161 item 3.
 */
import type { TFunction } from 'i18next';
import type { AllTimeCategoryTotals, Envelope } from '@/api/envelopes';
import { formatMoneyMinor } from '@/i18n/format';

/** The narrative sentence for the envelope's kind; a sharer also gets the published-terms sentence. */
export function envelopeNarrative(
  envelope: Envelope,
  currency: string | null,
  locale: string,
  t: TFunction,
  sharer: { owner: string; virtualAccount: string } | null,
): string {
  const money = (minor: number) => formatMoneyMinor(minor, currency, locale);
  // A target, budget or goal is in whole units (contract v2 §13, TMMAPP-0154 §A 2); what's spent, left
  // or over follows the balance and keeps its cents.
  const whole = (minor: number) =>
    formatMoneyMinor(minor, currency, locale, false, { whole: true });
  const balance = envelope.balanceMinor;
  const target = envelope.targetMinor;
  let text: string;
  if (envelope.kind === 'SAVINGS') {
    if (target === null) text = t('envelope.narrative.savingsNone');
    else if (balance === null)
      text = t('envelope.narrative.savingsBare', { target: whole(target) });
    else if (target - balance > 0) {
      text = t('envelope.narrative.savingsTo', {
        target: whole(target),
        balance: money(balance),
        left: money(target - balance),
      });
    } else {
      text = t('envelope.narrative.savingsReached', { balance: money(balance) });
    }
  } else if (envelope.kind === 'BUDGET') {
    const figures = envelope.periodFigures;
    const spent = figures?.spentMinor ?? null;
    const remaining = figures?.remainingMinor ?? null;
    if (target === null) text = t('envelope.narrative.budgetNone');
    else if (spent === null || remaining === null) {
      text = t('envelope.narrative.budgetBare', { target: whole(target) });
    } else if (remaining >= 0) {
      text = t('envelope.narrative.budgetLeft', {
        target: whole(target),
        spent: money(spent),
        left: money(remaining),
      });
    } else {
      text = t('envelope.narrative.budgetOver', {
        target: whole(target),
        spent: money(spent),
        over: money(-remaining),
      });
    }
  } else {
    text = t('envelope.narrative.cash');
  }
  return sharer === null
    ? text
    : `${text} ${t('envelope.narrative.published', { owner: sharer.owner, virtualAccount: sharer.virtualAccount })}`;
}

/** The "Categories in this envelope" card shows when the envelope has entries. */
export function hasCategoryTotals(totals: AllTimeCategoryTotals | null): boolean {
  return totals !== null && (totals.items.length > 0 || totals.uncategorizedMinor !== 0);
}
